"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@/utils/cn";

/* =====================
   DATA
===================== */
export type StatItem = {
  title: string;
  value: number;
  suffix?: string;
  image: string;
  useGrouping: boolean;
};

/* =====================
   COUNT UP
===================== */
function CountUp({
  target,
  suffix,
  start,
  useGrouping,
}: {
  target: number;
  suffix?: string;
  start: boolean;
  useGrouping: boolean;
}) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startTime: number | null = null;
    const duration = 1400;

    const animate = (time: number) => {
      if (!startTime) startTime = time;
      const progress = Math.min((time - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setValue(target * eased);
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [start, target]);

  const formatNumber = (num: number) => {
    if (target % 1 === 0) {
      return Math.round(num).toLocaleString("en-US", {
        useGrouping,
      });
    }

    return num.toFixed(2);
  };

  return (
    <>
      {formatNumber(value)}
      {suffix}
    </>
  );
}

/* =====================
   SECTION ONE
===================== */
interface SectionOneProps {
  STATS: StatItem[];
  bgImg: string;
  radius?: number;
  shadowOpacity?: number;
}

export default function SectionOne({
  bgImg,
  STATS,
  radius = 0,
  shadowOpacity = 0.5,
}: SectionOneProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      style={{
        borderTopLeftRadius: radius,
        borderTopRightRadius: radius,
      }}
      className="
        relative
        min-h-screen
        pt-40
        overflow-hidden
        z-20
      "
    >
      {/* BACKGROUND IMAGE */}
      <div className="absolute inset-0 -z-10">
        <Image
          src={bgImg}
          alt=""
          fill
          priority
          className="object-cover"
          sizes="(min-width: 1024px) 50vw, (min-width: 768px) 70vw, 90vw"
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* CINEMATIC TOP SHADOW */}
      <div
        style={{ opacity: shadowOpacity }}
        className="
          pointer-events-none
          absolute inset-x-0 top-0
          -translate-y-full
          h-48
          bg-gradient-to-t
          from-black/50
          via-black/20
          to-transparent
        "
      />

      {/* STATS CONTENT – GIỮ NGUYÊN STYLE */}
      <div className="relative z-10 py-24">
        <div
          className="
            mx-auto
            w-[90%] lg:w-[80%]
            grid grid-cols-1 md:grid-cols-2
            gap-8
          "
        >
          {STATS.map((item, i) => (
            <div
              key={i}
              className={cn(
                "group relative overflow-hidden",
                "rounded-xl bg-white",
                "border border-black/5",
                "shadow-[0_20px_50px_rgba(0,0,0,0.08)]",
                "transition-all duration-500",
                visible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8",
              )}
              style={{ transitionDelay: `${i * 320}ms` }}
            >
              {/* IMAGE */}
              <div className="relative w-full aspect-[21/9] overflow-hidden">
                <Image
                  src={item.image}
                  alt={item.title}
                  fill
                  className="
                    object-cover
                    transition-transform duration-700
                    group-hover:scale-105
                  "
                  sizes="(min-width: 1024px) 50vw, (min-width: 768px) 70vw, 90vw"
                />
                <div className="absolute inset-0 bg-black/5" />
              </div>

              {/* CONTENT */}
              <div className="flex flex-wrap items-end gap-y-2 px-6 py-4">
                {/* VALUE */}
                <div className="w-1/2 text-2xl md:text-3xl lg:text-4xl font-bold text-primary leading-tight">
                  <CountUp
                    target={item.value}
                    suffix={item.suffix}
                    start={visible}
                    useGrouping={item.useGrouping}
                  />
                </div>

                {/* TITLE */}
                <p className="w-1/2 text-right text-[12px] uppercase tracking-[0.2em] text-primary/90 leading-snug">
                  {item.title}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
